import Text from "@/components/Text";
import { FC, PropsWithChildren } from "react";
import styled, { css } from "styled-components";

type AlertType = "warning" | "success" | "error";

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 24px;
  height: 24px;
  transition: 0.2s ease-out opacity;
  &:hover {
    opacity: 0.75;
  }
`;

const StyledAlert = styled.div<{ $type: AlertType }>`
  border-radius: 4px;
  border-width: 1px;
  border-style: solid;
  padding: 16px 16px 24px;
  position: relative;

  ${({ theme, $type }) =>
    $type === "warning" &&
    css`
      background: ${theme.colors.warningLight};
      border-color: ${theme.colors.warning};
      ${CloseButton} {
        color: ${theme.colors.warning};
      }
    `}
  ${({ theme, $type }) =>
    $type === "success" &&
    css`
      background: ${theme.colors.successLight};
      border-color: ${theme.colors.success};
      ${CloseButton} {
        color: ${theme.colors.success};
      }
    `}
  ${({ theme, $type }) =>
    $type === "error" &&
    css`
      background: ${theme.colors.errorLight};
      border-color: ${theme.colors.error};
      ${CloseButton} {
        color: ${theme.colors.error};
      }
    `}
`;

const Heading = styled(Text)`
  margin: 0 0 12px;
`;

const Content = styled(Text)`
  ul {
    list-style-type: disc;
    list-style-position: inside;
  }
  ol {
    list-style-type: decimal;
    list-style-position: inside;
  }
  ul ul,
  ol ul {
    list-style-type: circle;
    list-style-position: inside;
    margin-left: 15px;
  }
  ol ol,
  ul ol {
    list-style-type: lower-latin;
    list-style-position: inside;
    margin-left: 15px;
  }
`;

interface Props {
  type: AlertType;
  headline: string;
  className?: string;
}

const Alert: FC<PropsWithChildren<Props>> = ({
  headline,
  type,
  children,
  className,
}) => {
  return (
    <StyledAlert className={className} $type={type}>
      <Heading $type="caption">{headline}</Heading>
      <Content as="div" $type="smallMedium">
        {children}
      </Content>
      <CloseButton>
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M15.6927 8.30762L8.30811 15.6922"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M15.6927 15.6922L8.30811 8.30762"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </CloseButton>
    </StyledAlert>
  );
};

export default Alert;
